import { Routes } from '@angular/router';
import { RoleEnum } from '@core/config/role.enum';
import { authGuardWithRedirectFn } from 'ok-angular-shared/auth';
import { canDeactivateGuard } from './shared/guards/can-deactivate.guard';
import { PageMinimumComponent } from './pages/page-minimum/page-minimum.component';
import { PageNotFoundComponent } from './shared/page-not-found/page-not-found.component';

export const routes: Routes = [
  {
    path: '',
    loadComponent: () => import('./pages/page-full/page-full.component').then(m => m.PageFullComponent),
    children: [
      {
        path: '',
        loadComponent: () => import('./home/home.component').then(m => m.HomeComponent),
        canActivate: [authGuardWithRedirectFn],
        title: 'Forside',
      },
      {
        path: 'device-lookup',
        loadComponent: () =>
          import('./device-lookup/components/device-lookup-overview/device-lookup-overview.component').then(
            m => m.DeviceLookupOverviewComponent
          ),
        canActivate: [authGuardWithRedirectFn],
        data: { roles: [RoleEnum.User, RoleEnum.SuperUser] },
        title: 'Enhedsopslag',
      },
      {
        path: 'messages',
        canActivate: [authGuardWithRedirectFn],
        data: { roles: [RoleEnum.MessageReader, RoleEnum.MessageEditor, RoleEnum.MessageApprover, RoleEnum.MessageDeveloper] },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./messages/components/messages-overview/messages-overview.component').then(m => m.MessagesOverviewComponent),
            title: 'Beskeder',
          },
          {
            path: 'create',
            loadComponent: () =>
              import('./messages/components/message-create/message-create.component').then(m => m.MessageCreateComponent),
            data: { roles: [RoleEnum.MessageEditor, RoleEnum.MessageApprover, RoleEnum.MessageDeveloper] },
            canDeactivate: [canDeactivateGuard],
            title: 'Opret besked',
          },
          {
            path: ':id/transmission-data',
            loadComponent: () =>
              import('./messages/components/transmission-data-overview/transmission-data-overview.component').then(
                m => m.TransmissionDataOverviewComponent
              ),
            title: 'Udsendelser & statistik',
          },
          {
            path: ':id/transmission-data/:transmissionId/edit',
            loadComponent: () =>
              import('./messages/components/edit-transmission-data/edit-transmission-data.component').then(
                m => m.EditTransmissionDataComponent
              ),
            data: { roles: [RoleEnum.MessageApprover, RoleEnum.MessageDeveloper] },
            canDeactivate: [canDeactivateGuard],
            title: 'Rediger udsendelse',
          },
          {
            path: ':id',
            loadComponent: () => import('./messages/components/message-edit/message-edit.component').then(m => m.MessageEditComponent),
            data: { roles: [RoleEnum.MessageEditor, RoleEnum.MessageApprover, RoleEnum.MessageDeveloper] },
            canDeactivate: [canDeactivateGuard],
            title: 'Redigér besked',
          },
        ],
      },
      {
        path: 'push-templates',
        canActivate: [authGuardWithRedirectFn],
        data: { roles: [RoleEnum.MessageReader, RoleEnum.MessageEditor, RoleEnum.MessageApprover, RoleEnum.MessageDeveloper] },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./push-templates/components/push-templates-overview/push-templates-overview.component').then(
                m => m.PushTemplatesOverviewComponent
              ),
            title: 'Push skabeloner',
          },
          {
            path: 'create',
            loadComponent: () =>
              import('./push-templates/components/push-templates-edit/push-templates-edit.component').then(
                m => m.PushTemplatesEditComponent
              ),
            data: { roles: [RoleEnum.MessageEditor, RoleEnum.MessageApprover, RoleEnum.MessageDeveloper] },
            canDeactivate: [canDeactivateGuard],
            title: 'Opret push skabelon',
          },
          {
            path: 'copy/:id',
            loadComponent: () =>
              import('./push-templates/components/push-templates-edit/push-templates-edit.component').then(
                m => m.PushTemplatesEditComponent
              ),
            data: { roles: [RoleEnum.MessageEditor, RoleEnum.MessageApprover, RoleEnum.MessageDeveloper] },
            canDeactivate: [canDeactivateGuard],
            title: 'Kopiér push skabelon',
          },
          {
            path: 'edit/:id',
            loadComponent: () =>
              import('./push-templates/components/push-templates-edit/push-templates-edit.component').then(
                m => m.PushTemplatesEditComponent
              ),
            data: { roles: [RoleEnum.MessageEditor, RoleEnum.MessageApprover, RoleEnum.MessageDeveloper] },
            canDeactivate: [canDeactivateGuard],
            title: 'Redigér push skabelon',
          },
          {
            path: 'view/:id',
            loadComponent: () =>
              import('./push-templates/components/push-templates-edit/push-templates-edit.component').then(
                m => m.PushTemplatesEditComponent
              ),
            data: { viewMode: true },
            title: 'Push skabelon detaljer',
          },
        ],
      },
      {
        path: 'push-messages',
        canActivate: [authGuardWithRedirectFn],
        data: { roles: [RoleEnum.MessageReader, RoleEnum.MessageEditor, RoleEnum.MessageApprover, RoleEnum.MessageDeveloper] },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./push-messages/components/push-messages-overview/push-messages-overview.component').then(
                m => m.PushMessagesOverviewComponent
              ),
            title: 'Push beskeder',
          },
          {
            path: 'create',
            loadComponent: () =>
              import('./push-messages/components/push-message-edit/push-message-edit.component').then(m => m.PushMessageEditComponent),
            data: { roles: [RoleEnum.MessageEditor, RoleEnum.MessageApprover, RoleEnum.MessageDeveloper] },
            canDeactivate: [canDeactivateGuard],
            title: 'Opret push',
          },
          {
            path: 'copy/:id',
            loadComponent: () =>
              import('./push-messages/components/push-message-edit/push-message-edit.component').then(m => m.PushMessageEditComponent),
            data: { roles: [RoleEnum.MessageEditor, RoleEnum.MessageApprover, RoleEnum.MessageDeveloper], copy: true },
            canDeactivate: [canDeactivateGuard],
            title: 'Kopiér push',
          },
          {
            path: ':id',
            loadComponent: () =>
              import('./push-messages/components/push-message-edit/push-message-edit.component').then(m => m.PushMessageEditComponent),
            data: { roles: [RoleEnum.MessageEditor, RoleEnum.MessageApprover, RoleEnum.MessageDeveloper] },
            canDeactivate: [canDeactivateGuard],
            title: 'Redigér push',
          },
        ],
      },
      {
        path: 'consent',
        canActivate: [authGuardWithRedirectFn],
        data: { roles: [RoleEnum.MessageReader, RoleEnum.MessageEditor, RoleEnum.MessageApprover, RoleEnum.MessageDeveloper] },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./consent/components/consent-overview/consent-overview.component').then(m => m.ConsentOverviewComponent),
            title: 'Samtykke',
          },
          {
            path: 'create',
            loadComponent: () => import('./consent/components/consent-create/consent-create.component').then(m => m.ConsentCreateComponent),
            data: { roles: [RoleEnum.MessageEditor, RoleEnum.MessageApprover, RoleEnum.MessageDeveloper] },
            canDeactivate: [canDeactivateGuard],
            title: 'Opret samtykketype',
          },
          {
            path: ':id',
            loadComponent: () => import('./consent/components/consent-edit/consent-edit.component').then(m => m.ConsentEditComponent),
            data: { roles: [RoleEnum.MessageEditor, RoleEnum.MessageApprover, RoleEnum.MessageDeveloper] },
            canDeactivate: [canDeactivateGuard],
            title: 'Redigér samtykketype',
          },
        ],
      },
    ],
  },
  {
    path: 'not-found',
    component: PageMinimumComponent,
    children: [
      {
        path: '',
        pathMatch: 'full',
        component: PageNotFoundComponent,
        title: 'Siden blev ikke fundet',
      },
    ],
  },
  {
    path: 'unauthorized',
    title: 'Ingen adgang',
    loadComponent: () => import('./pages/no-access/no-access.component').then(m => m.NoAccessComponent),
  },
  {
    path: '**', // Must be last!
    redirectTo: '/not-found',
  },
];
